import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { refreshUser } from 'features/auth/authSlice';
// import { NotInitial } from 'features/admin/appSettingsAPI';
import { deleteFile, postFile } from 'features/files/fileAPI';
import { FolderLocation, getStorageFolderName } from 'features/files/fileSlice';
import { IPage } from 'features/page/pageAPI';
import { IProject } from 'features/project/projectAPI';
import { IMember } from 'features/team/memberAPI';
import { Locale } from 'helpers/consts';
import { sameId } from 'helpers/objects';
import {
  IUser,
  IUserWithIsGuest,
  patchUserData,
  postUserData,
  postVerificationCode,
  resetPwd,
  Role,
  userForgot,
  verifyMail,
} from './userAPI';

export interface IUserState {
  status: 'idle' | 'loading' | 'failed';
  errors: any;
}

export type UserStateProps = {
  userIsAdmin: boolean;
  userIsManager: boolean;
  locale: Locale;
};

export function canEdit(
  user: IUser,
  object: IPage | IProject | IMember | null,
): boolean {
  const o = object;
  if (!o || !user || !user.roles) return false;
  const owner = 'author' in o ? o.author : 'user' in o ? o.user : null;
  return (
    (user.roles.includes(Role.admin) ||
      user.roles.includes(Role.manager) ||
      sameId(owner?.id, user.id)) ??
    false
  );
}

export interface IDecodedResponse {
  [key: string]: any;
}

export interface ISignupData {
  username: string;
  email: string;
  password: string;
}

interface ILoginData {
  email: string;
  token: string;
  password: string;
}
interface IForgotData {
  email: string;
}
interface IVerifyData {
  token: string;
}
interface IVerifyCode {
  userId: number;
  code: string;
}

export const emptyUser: IUser = {
  username: '',
  email: '',
  roles: [],
  avatar: null,
  member: null,
  pages: [],
  projects: [],
  uploadedFiles: [],
};

export const initialState: IUserState = {
  status: 'idle',
  errors: '',
};

export const signupUserAsync = createAsyncThunk(
  'user/postUserData',
  async (
    { data, noEmail = false }: { data: ISignupData; noEmail?: boolean },
    thunkAPI,
  ) => {
    try {
      return { response: await postUserData(data, noEmail) };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const userForgotAsync: any = createAsyncThunk(
  'user/userForgot',
  async (data: IForgotData, thunkAPI) => {
    try {
      return { response: await userForgot(data) };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const resetPwdAsync: any = createAsyncThunk(
  'user/resetPwd',
  async (data: ILoginData, thunkAPI) => {
    try {
      return { response: await resetPwd(data) };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const verifyMailAsync: any = createAsyncThunk(
  'user/verifyMail',
  async (data: IVerifyData, thunkAPI) => {
    try {
      const res = await verifyMail(data);
      return { response: res };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const postVerificationCodeAsync: any = createAsyncThunk(
  'user/postVerificationCode',
  async (data: IVerifyCode, thunkAPI) => {
    try {
      const res = await postVerificationCode(data);
      return { response: res };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const patchUserAsync = createAsyncThunk(
  'user/patchUserData',
  async (
    {
      data,
      file,
      user,
    }: {
      data: IUserWithIsGuest;
      file?: File;
      user?: IUser;
    },
    thunkAPI,
  ) => {
    if (!data.id) data.id = user?.id;
    try {
      if (file || !data.avatar) {
        // we may be deleting the avatar
        // if there was an avatar, it needs to be deleted
        if (user?.avatar) {
          // 1.a from storage: TODO
          // 1.b from the db
          if (typeof user.avatar === 'object') await deleteFile(user.avatar.id);
          else await deleteFile(user.avatar);
        }
      }
      if (file) {
        // we have a new avatar
        // upload the new file
        const fd = await postFile(file, {
          description: 'Avatar',
          storage: getStorageFolderName(FolderLocation.Avatars),
          size: file.size,
          mimeType: file.type,
        });
        // keep the new file's id and uri
        data.avatar = { id: fd.data._id, uri: fd.data.message };
      }
      // update the user
      const response = await patchUserData(data);
      // Automatically refresh the auth user state after successful update
      await thunkAPI.dispatch(refreshUser());
      return { response: response };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder
      .addCase(signupUserAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(signupUserAsync.fulfilled, (state, action) => {
        state.status = 'idle';
      })
      .addCase(signupUserAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.errors = action.payload;
      })
      .addCase(userForgotAsync.pending, (state) => {
        // state.status = 'loading';
      })
      .addCase(userForgotAsync.fulfilled, (state, action) => {
        // state.status = 'idle';
      })
      .addCase(userForgotAsync.rejected, (state, action) => {
        // state.status = 'failed';
        // state.errors = action.payload;
      })
      .addCase(resetPwdAsync.pending, (state) => {
        // state.status = 'loading';
      })
      .addCase(resetPwdAsync.fulfilled, (state, action) => {
        // state.status = 'idle';
      })
      .addCase(resetPwdAsync.rejected, (state, action) => {
        // state.status = 'failed';
        // state.errors = action.payload;
      })
      .addCase(verifyMailAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(verifyMailAsync.fulfilled, (state, action) => {
        state.status = 'idle';
      })
      .addCase(verifyMailAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.errors = action.payload;
      })
      .addCase(patchUserAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(patchUserAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        // Note: refreshUser is automatically dispatched in the thunk above
      })
      .addCase(patchUserAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.errors = action.payload;
      });
  },
});

export const userActions = userSlice.actions;

export default userSlice.reducer;
